import { lazy, Suspense, useCallback } from 'react';

import LayoutDrawer from 'approot/shared/layout/layout-drawer';
import { useGetInteractiveLessonParams } from '../../../activity-builder.hooks';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import {
  PresentDrawerItem,
  UpdateActiveSectionProps,
} from 'approot/activity-builder/present/activity-builder-present.constants';
import {
  ACTIVITY_BUILDER_EVENT_ACTIVITIES_NAV_ITEM,
  getUpdatedStudentSectionDetails,
} from 'approot/activity-builder/present/activity-builder-present-data-tracking.utils';
import { StudentSectionOrSubSectionOverview } from '../../student.constants';
import * as Styled from 'approot/activity-builder/present/menu/present-menu.style';
import { useGetStudentAssessmentAssignmentById } from '../../../../student-assessment/hooks/use-get-student-assessment-assignment-by-id';
import StudentPresentMenu from './student-present-menu';
import StudentPresentMenuActivities from './student-present-menu-activities';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ASSESSMENT_ACTIVITIES } from 'global/constants';
import StudentPresentMenuTranslate from './student-present-menu-translate';
import { ActivityAnswerPrintContextProvider } from 'approot/activity-builder/print/answers/activity-answers-print.context';
import { useGetStudentDataParams } from 'approot/students/logic/use-get-student-data-params';

const DownloadStudentAssignmentAnswers = lazy(
  () =>
    import('approot/assignments/download/download-student-assignment-answers')
);

type Props = {
  contentId: number;
  isLoggedIn: boolean;
  activeSectionId: string;
  sectionsOverview: StudentSectionOrSubSectionOverview[];
  currentSectionOverview: StudentSectionOrSubSectionOverview | null;
  currentSectionOverviewIndex: number;
  updateActiveSection: (props: UpdateActiveSectionProps) => void;
};

const StudentPresentMenuAssessment = ({
  contentId,
  isLoggedIn,
  activeSectionId,
  sectionsOverview,
  currentSectionOverview,
  currentSectionOverviewIndex,
  updateActiveSection,
}: Props) => {
  const { drawer, drawerType, setDrawer, isZoomToFit } =
    useActivityBuilderPresentContext();

  const {
    contentType,
    version,
    lang: language,
    assignmentId,
    classId,
  } = useGetInteractiveLessonParams();

  const { teacherClassId } = useGetStudentDataParams();

  const { assignment, assignedActivities, linkedContent } =
    useGetStudentAssessmentAssignmentById({
      fetchPolicy: 'cache-and-network',
      assignmentId,
      classId,
      language,
    });

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleClose = useCallback(() => {
    setDrawer({
      isActive: true,
      type: PresentDrawerItem.None,
    });
  }, [setDrawer]);

  return (
    <>
      {isVisible &&
        assignedActivities &&
        assignment &&
        version &&
        linkedContent && (
          <Suspense fallback={<div />}>
            <ActivityAnswerPrintContextProvider
              classId={teacherClassId}
              contentType={ASSESSMENT_ACTIVITIES}
              contentId={assignment.contentId}
              showAnswer={false}
            >
              <DownloadStudentAssignmentAnswers
                language={language}
                linkedContent={linkedContent}
                version={version}
                contentId={assignment.contentId}
                contentType={ASSESSMENT_ACTIVITIES}
                classId={teacherClassId}
                assignmentId={assignmentId}
                isAuthenticated={isLoggedIn}
                activities={assignedActivities}
                handleClose={closeModal}
              />
            </ActivityAnswerPrintContextProvider>
          </Suspense>
        )}

      <Styled.PresentMenuDetailDrawer $isActive={drawer.isDetailActive}>
        <LayoutDrawer
          isSmall
          isActive={drawerType === PresentDrawerItem.Activities}
          noScrollOnContainer
          captureKeypress={false}
          hasUnderlay={false}
          dataEvent={undefined}
          onClose={handleClose}
        >
          {drawerType === PresentDrawerItem.Activities &&
            assignedActivities &&
            assignment?.contentId &&
            linkedContent && (
              <StudentPresentMenuActivities
                linkedContent={linkedContent}
                contentId={assignment.contentId}
                contentType={contentType}
                version={version}
                language={language}
                data={assignedActivities}
                activeSectionId={activeSectionId}
                onClose={handleClose}
                isZoomToFit={isZoomToFit}
                onActivityClick={sectionId => {
                  const { newSectionOverview, index } =
                    getUpdatedStudentSectionDetails(
                      sectionId,
                      sectionsOverview
                    );
                  updateActiveSection({
                    sectionId,
                    newSectionOverview,
                    index,
                    eventType: ACTIVITY_BUILDER_EVENT_ACTIVITIES_NAV_ITEM,
                  });
                }}
              />
            )}
        </LayoutDrawer>

        <LayoutDrawer
          isSmall
          isActive={drawerType === PresentDrawerItem.Translate}
          noScrollOnContainer
          captureKeypress={false}
          hasUnderlay={false}
          dataEvent={undefined}
          onClose={handleClose}
        >
          {drawerType === PresentDrawerItem.Translate &&
            version &&
            assignedActivities && (
              <StudentPresentMenuTranslate
                version={version}
                contentType={contentType}
                lang={language}
                availableTranslations={
                  assignedActivities?.availableTranslations
                }
                onClose={handleClose}
              />
            )}
        </LayoutDrawer>
      </Styled.PresentMenuDetailDrawer>
      {drawer.isActive && (
        <StudentPresentMenu
          activeSectionId={activeSectionId}
          currentSectionOverview={currentSectionOverview}
          currentSectionOverviewIndex={currentSectionOverviewIndex}
          sectionsOverview={sectionsOverview}
          isLoggedIn={isLoggedIn}
          handleDownload={openModal}
          handleOpen={type =>
            setDrawer({
              isActive: true,
              type,
            })
          }
        />
      )}
    </>
  );
};

export default StudentPresentMenuAssessment;
